import type { VRMCore } from '@pixiv/three-vrm-core'

import { ref } from 'vue'

interface EmotionState {
  expression?: {
    name: string
    value: number
    duration?: number
    curve?: (t: number) => number
  }[]
  blendDuration?: number
}

export function useVRMEmote(vrm: VRMCore) {
  const currentEmotion = ref<string | null>(null)
  const isTransitioning = ref(false)
  const transitionProgress = ref(0)
  const currentExpressionValues = ref(new Map<string, number>())
  const targetExpressionValues = ref(new Map<string, number>())
  const resetTimeout = ref<number>()

  // Utility functions
  const lerp = (start: number, end: number, t: number): number => {
    return start + (end - start) * t
  }

  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
  }

  const clampIntensity = (value: number): number => {
    return Math.min(1, Math.max(0, value))
  }

  const randomBetween = (min: number, max: number) => {
    return Math.round(min + Math.random() * (max - min))
  }

  const isBlinkPreset = (expressionName: string) => {
    return expressionName === 'blinkLeft' || expressionName === 'blinkRight'
  }

  const resolveEmotionExpressions = (emotionName: string, emotionState: EmotionState) => {
    const expressions = [...(emotionState.expression || [])]
    if (emotionName !== 'happy' || expressions.length === 0)
      return expressions

    if (Math.random() >= 0.15)
      return expressions

    const primaryExpression = expressions[0]
    if (!primaryExpression || primaryExpression.name !== 'happy')
      return expressions

    const winkPreset = Math.random() < 0.5 ? 'blinkLeft' : 'blinkRight'
    return [
      { ...primaryExpression, name: winkPreset, value: 1.0 },
      ...expressions.slice(1),
    ]
  }

  const resolveResetDurationMs = (requestedMs: number, expressions: Array<{ name: string }>) => {
    if (expressions.some(expr => isBlinkPreset(expr.name))) {
      return randomBetween(180, 280)
    }

    return requestedMs
  }

  // Emotion states definition — values are the "full weight" targets;
  // actual applied weight is value × clamped intensity.
  // Using slightly lower values (0.7–0.8) for primary expressions to
  // prevent the "too raw / smiles too much" problem reported in #590.
  const emotionStates = new Map<string, EmotionState>([
    ['happy', {
      expression: [
        { name: 'happy', value: 1.0, duration: 0.3 },
      ],
      blendDuration: 0.4,
    }],
    ['sad', {
      expression: [
        { name: 'sad', value: 0.7 },
        { name: 'oh', value: 0.15 },
      ],
      blendDuration: 0.4,
    }],
    ['angry', {
      expression: [
        { name: 'angry', value: 0.7 },
        { name: 'ee', value: 0.3 },
      ],
      blendDuration: 0.3,
    }],
    ['surprised', {
      expression: [
        { name: 'surprised', value: 0.6 },
        { name: 'oh', value: 0.4 },
      ],
      blendDuration: 0.15,
    }],
    ['neutral', {
      expression: [],
      blendDuration: 0.6,
    }],
  ])

  const clearResetTimeout = () => {
    if (resetTimeout.value) {
      clearTimeout(resetTimeout.value)
      resetTimeout.value = undefined
    }
  }

  const setEmotion = (
    emotionName: string,
    intensity = 1,
    resolvedExpressions?: Array<{ name: string, value: number, duration?: number, curve?: (t: number) => number }>,
  ) => {
    clearResetTimeout()

    if (!emotionStates.has(emotionName)) {
      console.warn(`Emotion ${emotionName} not found`)
      return
    }

    const emotionState = emotionStates.get(emotionName)!
    currentEmotion.value = emotionName
    isTransitioning.value = true
    transitionProgress.value = 0

    // Store current expression values as starting point BEFORE resetting,
    // so the lerp transition starts from the actual displayed values
    // instead of snapping to 0 first (fixes #590).
    currentExpressionValues.value.clear()
    targetExpressionValues.value.clear()

    const normalizedIntensity = clampIntensity(intensity)

    if (vrm.expressionManager) {
      // Capture current values for all expressions we'll be transitioning
      const expressionNames = Object.keys(vrm.expressionManager.expressionMap)
      for (const name of expressionNames) {
        const currentValue = vrm.expressionManager.getValue(name) || 0
        currentExpressionValues.value.set(name, currentValue)
        // Default target is 0 for expressions not in the target emotion
        targetExpressionValues.value.set(name, 0)
      }
    }

    // Override target values for specified expressions in the emotion state
    for (const expr of resolvedExpressions || resolveEmotionExpressions(emotionName, emotionState)) {
      targetExpressionValues.value.set(expr.name, expr.value * normalizedIntensity)
    }
  }

  const setEmotionWithResetAfter = (emotionName: string, ms: number, intensity = 1) => {
    clearResetTimeout()
    const emotionState = emotionStates.get(emotionName)
    const resolvedExpressions = emotionState ? resolveEmotionExpressions(emotionName, emotionState) : []
    const resetAfterMs = resolveResetDurationMs(ms, resolvedExpressions)
    setEmotion(emotionName, intensity, resolvedExpressions)

    // Reset to a neutral state by clearing transient expression weights.
    resetTimeout.value = setTimeout(() => {
      setEmotion('neutral')
      resetTimeout.value = undefined
    }, resetAfterMs) as unknown as number
  }

  const update = (deltaTime: number) => {
    if (!isTransitioning.value || !currentEmotion.value)
      return

    const emotionState = emotionStates.get(currentEmotion.value)!
    const blendDuration = emotionState.blendDuration || 0.3

    transitionProgress.value += deltaTime / blendDuration
    if (transitionProgress.value >= 1.0) {
      transitionProgress.value = 1.0
      isTransitioning.value = false
    }

    // Update all expressions
    for (const [exprName, targetValue] of targetExpressionValues.value) {
      const startValue = currentExpressionValues.value.get(exprName) || 0
      const currentValue = lerp(
        startValue,
        targetValue,
        easeInOutCubic(transitionProgress.value),
      )
      vrm.expressionManager?.setValue(exprName, currentValue)
    }
  }

  const addEmotionState = (emotionName: string, state: EmotionState) => {
    emotionStates.set(emotionName, state)
  }

  const removeEmotionState = (emotionName: string) => {
    emotionStates.delete(emotionName)
  }

  // Cleanup function
  const dispose = () => {
    clearResetTimeout()
  }

  return {
    currentEmotion,
    isTransitioning,
    setEmotion,
    setEmotionWithResetAfter,
    update,
    addEmotionState,
    removeEmotionState,
    dispose,
  }
}
